import { useState, useCallback } from 'react';
import { fetchTeamPlayerData } from '@/services/reports/reportService';
import { TeamPlayerCount, TeamPlayerRawData } from '@/types/reports'; // Adjusted import path if index.ts is not the primary export for reports.ts
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, Download, ChevronUp, ChevronDown } from 'lucide-react';

type TeamPlayerSortField = 'team_name' | 'gender'; // Sorting by counts can be added here if needed
type SortDirection = 'asc' | 'desc';

export default function TeamPlayerCountsReport() {
  const [teamPlayerCounts, setTeamPlayerCounts] = useState<TeamPlayerCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportGenerated, setReportGenerated] = useState(false);

  const [sortField, setSortField] = useState<TeamPlayerSortField>('gender');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const processData = useCallback((rawData: TeamPlayerRawData[]): TeamPlayerCount[] => {
    const counts: Record<string, TeamPlayerCount> = {};
    rawData.forEach(item => {
      const teamInfo = item.teams;
      if (teamInfo && teamInfo.name && teamInfo.sex && item.status) {
        const key = `${teamInfo.name}-${teamInfo.sex}`;
        if (!counts[key]) {
          counts[key] = {
            team_name: teamInfo.name,
            gender: teamInfo.sex,
            considering_count: 0,
            offered_count: 0,
            rostered_count: 0,
            declined_count: 0,
          };
        }
        // Normalize status to lowercase for case-insensitive matching
        const currentStatus = item.status.toLowerCase(); 
        if (currentStatus === 'considering') {
          counts[key].considering_count++;
        } else if (currentStatus === 'offered') {
          counts[key].offered_count++;
        } else if (currentStatus === 'rostered') {
          counts[key].rostered_count++;
        } else if (currentStatus === 'declined') {
          counts[key].declined_count++;
        } else {
          console.warn(`Unknown status: ${item.status} for team ${teamInfo.name}, gender ${teamInfo.sex}`);
        }
      } else {
        console.warn('Skipping item due to missing or incomplete team/status data:', item);
      }
    });
    return Object.values(counts);
  }, []);

  async function fetchReportData() {
    setLoading(true);
    setError(null);
    setReportGenerated(true);
    setTeamPlayerCounts([]);

    try {
      const rawData = await fetchTeamPlayerData();
      const processedData = processData(rawData);
      setTeamPlayerCounts(processedData);
    } catch (err: any) {
      console.error('Error fetching team player counts:', err);
      setError(err.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  }

  const handleSort = (field: TeamPlayerSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedData = useCallback(() => {
    const sortedData = [...teamPlayerCounts].sort((a, b) => {
      const valA = a[sortField];
      const valB = b[sortField];

      let comparison = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        comparison = valA - valB;
      } else if (typeof valA === 'string' && typeof valB === 'string') {
        comparison = valA.localeCompare(valB);
      } else {
        comparison = String(valA).localeCompare(String(valB));
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sortedData;
  }, [teamPlayerCounts, sortField, sortDirection]);

  const emptyTotals = () => ({
    considering_count: 0,
    offered_count: 0,
    rostered_count: 0,
    declined_count: 0,
  });

  const addToTotals = (
    acc: ReturnType<typeof emptyTotals>,
    item: TeamPlayerCount
  ) => ({
    considering_count: acc.considering_count + item.considering_count,
    offered_count: acc.offered_count + item.offered_count,
    rostered_count: acc.rostered_count + item.rostered_count,
    declined_count: acc.declined_count + item.declined_count,
  });

  const getTotals = useCallback(() => {
    return teamPlayerCounts.reduce(addToTotals, emptyTotals());
  }, [teamPlayerCounts]);

  const getSubtotalsByGender = useCallback(() => {
    const byGender = new Map<string, ReturnType<typeof emptyTotals>>();
    teamPlayerCounts.forEach(item => {
      const current = byGender.get(item.gender) ?? emptyTotals();
      byGender.set(item.gender, addToTotals(current, item));
    });
    return [...byGender.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([gender, totals]) => ({ gender, ...totals }));
  }, [teamPlayerCounts]);

  const exportCSV = () => {
    if (teamPlayerCounts.length === 0) return;

    const headers = ['Team Name', 'Gender', 'Considering', 'Offered', 'Rostered', 'Declined'];
    const sortedData = getSortedData();
    const subtotals = getSubtotalsByGender();
    const totals = getTotals();

    const csvContent = [
      headers.join(','),
      ...sortedData.map(item => {
        return [
          `"${item.team_name}"`,
          `"${item.gender}"`,
          item.considering_count,
          item.offered_count,
          item.rostered_count,
          item.declined_count
        ].join(',');
      }),
      ...subtotals.map(sub => [
        `"Subtotal"`,
        `"${sub.gender}"`,
        sub.considering_count,
        sub.offered_count,
        sub.rostered_count,
        sub.declined_count,
      ].join(',')),
      [
        '"Total"',
        '""',
        totals.considering_count,
        totals.offered_count,
        totals.rostered_count,
        totals.declined_count,
      ].join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `team_player_counts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = ({ field }: { field: TeamPlayerSortField }) => {
    if (sortField !== field) return <ChevronDown className="ml-1 h-4 w-4 text-gray-400 opacity-50" />;
    return sortDirection === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team Player Counts</CardTitle>
        <CardDescription>Number of players per team and gender, broken down by status (Considering, Offered, Rostered, Declined).</CardDescription>
        <div className="flex justify-end space-x-2 pt-2">
           <Button onClick={fetchReportData} disabled={loading}>
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</> : 'Generate Report'}
          </Button>
          {reportGenerated && teamPlayerCounts.length > 0 && (
            <Button variant="outline" onClick={exportCSV} disabled={loading || teamPlayerCounts.length === 0}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <p className="ml-2 text-gray-600">Loading team player counts...</p>
          </div>
        )}
        {error && !loading && (
          <Alert variant="destructive" className="my-4">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load team player counts: {error}
            </AlertDescription>
          </Alert>
        )}
        {reportGenerated && !loading && !error && teamPlayerCounts.length === 0 && (
          <Alert className="my-4">
            <AlertTitle>No Data</AlertTitle>
            <AlertDescription>
              No team player count data available to display.
            </AlertDescription>
          </Alert>
        )}
        {reportGenerated && !loading && !error && teamPlayerCounts.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('team_name')}>
                  Team Name <SortIcon field="team_name" />
                </TableHead>
                <TableHead className="cursor-pointer hover:bg-muted/50" onClick={() => handleSort('gender')}>
                  Gender <SortIcon field="gender" />
                </TableHead>
                <TableHead className="text-right">Considering</TableHead>
                <TableHead className="text-right">Offered</TableHead>
                <TableHead className="text-right">Rostered</TableHead>
                <TableHead className="text-right">Declined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedData().map((item, index) => (
                <TableRow key={`${item.team_name}-${item.gender}-${index}`}>
                  <TableCell>{item.team_name}</TableCell>
                  <TableCell>{item.gender}</TableCell>
                  <TableCell className="text-right">{item.considering_count}</TableCell>
                  <TableCell className="text-right">{item.offered_count}</TableCell>
                  <TableCell className="text-right">{item.rostered_count}</TableCell>
                  <TableCell className="text-right">{item.declined_count}</TableCell>
                </TableRow>
              ))}
              {(() => {
                const subtotals = getSubtotalsByGender();
                const totals = getTotals();
                return (
                  <>
                    {subtotals.map((sub, idx) => (
                      <TableRow
                        key={`subtotal-${sub.gender}`}
                        className={`font-medium bg-muted/30${idx === 0 ? ' border-t-2' : ''}`}
                      >
                        <TableCell>Subtotal</TableCell>
                        <TableCell>{sub.gender}</TableCell>
                        <TableCell className="text-right">{sub.considering_count}</TableCell>
                        <TableCell className="text-right">{sub.offered_count}</TableCell>
                        <TableCell className="text-right">{sub.rostered_count}</TableCell>
                        <TableCell className="text-right">{sub.declined_count}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold border-t-2 bg-muted/50">
                      <TableCell>Total</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right">{totals.considering_count}</TableCell>
                      <TableCell className="text-right">{totals.offered_count}</TableCell>
                      <TableCell className="text-right">{totals.rostered_count}</TableCell>
                      <TableCell className="text-right">{totals.declined_count}</TableCell>
                    </TableRow>
                  </>
                );
              })()}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
